import React from "react";
import { Info } from "lucide-react";

const inputCls = "w-full bg-white border border-oth-line rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors";
const WIDE_TYPES = new Set(["textarea", "scale", "choice", "children"]);

const ScaleInput = ({ q, value, onChange }) => {
    const min = q.min ?? 0, max = q.max ?? 10;
    const opts = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
        <div className="flex flex-wrap gap-2">
            {opts.map((n) => (
                <button
                    key={n} type="button"
                    onClick={() => onChange(n)}
                    className={`w-9 h-9 rounded-full border text-sm font-body transition-colors ${value === n ? "bg-oth-blue text-white border-oth-blue" : "bg-white border-oth-line text-oth-ink2 hover:border-oth-blue"}`}
                    data-testid={`profile-scale-${q.id}-${n}`}
                >{n}</button>
            ))}
        </div>
    );
};

const ChoiceInput = ({ q, value, onChange }) => (
    <div className="flex flex-wrap gap-2">
        {(q.options || []).map((opt) => (
            <button
                key={opt} type="button"
                onClick={() => onChange(opt)}
                className={`px-4 py-2 rounded-full border text-sm font-body transition-colors ${value === opt ? "bg-oth-blue text-white border-oth-blue" : "bg-white border-oth-line text-oth-ink2 hover:border-oth-blue"}`}
                data-testid={`profile-choice-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            >{opt}</button>
        ))}
    </div>
);

const ChildrenInput = ({ q, value, onChange }) => {
    const v = value && typeof value === "object" ? value : { count: 0, ages: [] };
    const setCount = (count) => {
        const ages = Array.from({ length: count }, (_, i) => v.ages?.[i] ?? "");
        onChange(count === 0 ? { count: 0, ages: [] } : { count, ages });
    };
    return (
        <div className="space-y-3">
            <select
                className={`${inputCls} w-44`}
                value={v.count || 0}
                onChange={(e) => setCount(Number(e.target.value))}
                data-testid={`profile-children-count-${q.id}`}
            >
                {Array.from({ length: 9 }, (_, n) => <option key={n} value={n}>{n === 0 ? "No children" : `${n} ${n === 1 ? "child" : "children"}`}</option>)}
            </select>
            {v.count > 0 && (
                <div className="flex flex-wrap gap-3">
                    {v.ages.map((age, i) => (
                        <label key={i} className="block">
                            <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">Child {i + 1} age</div>
                            <input
                                type="number" min={0} max={60}
                                className={`${inputCls} w-28`}
                                value={age}
                                onChange={(e) => onChange({ ...v, ages: v.ages.map((a, idx) => (idx === i ? (e.target.value === "" ? "" : Number(e.target.value)) : a)) })}
                                data-testid={`profile-child-age-${q.id}-${i}`}
                            />
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

const QuestionField = ({ q, value, onChange, error }) => {
    const wide = WIDE_TYPES.has(q.type);
    const shortInputCls = q.type === "number" ? `${inputCls} max-w-[160px]` : inputCls;
    return (
        <div className={wide ? "md:col-span-2 pt-5 mt-1 border-t border-oth-line/50" : ""}>
            <div className="flex items-center gap-1.5 font-body text-sm font-medium text-oth-ink mb-2">
                <span>{q.label}{q.required && <span className="text-red-600 ml-0.5">*</span>}</span>
                {q.hint && (
                    <span className="relative group inline-flex" data-testid={`profile-tip-${q.id}`}>
                        <Info size={13} className="text-oth-ink3 cursor-help shrink-0" />
                        <span className="pointer-events-none absolute left-0 top-5 z-30 hidden group-hover:block w-72 font-normal text-[12px] leading-snug bg-[#0A0A0A] text-white rounded-xl px-3 py-2 shadow-lg">
                            {q.hint}
                        </span>
                    </span>
                )}
            </div>
            {q.type === "textarea" ? (
                <textarea className={`${inputCls} min-h-[90px]`} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`profile-q-${q.id}`} />
            ) : q.type === "scale" ? (
                <ScaleInput q={q} value={value} onChange={onChange} />
            ) : q.type === "choice" ? (
                <ChoiceInput q={q} value={value} onChange={onChange} />
            ) : q.type === "children" ? (
                <ChildrenInput q={q} value={value} onChange={onChange} />
            ) : (
                <input
                    className={shortInputCls}
                    type={q.type === "number" ? "number" : q.type === "email" ? "email" : q.type === "phone" ? "tel" : "text"}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`profile-q-${q.id}`}
                />
            )}
            {error && <div className="text-xs text-red-600 font-body mt-1.5">{error}</div>}
        </div>
    );
};

export default function StepProfile({ questions, profile, setProfile, errors }) {
    return (
        <div className="rounded-3xl border border-oth-line/70 bg-white p-6 md:p-8" data-testid="planner-step-profile">
            <h3 className="font-display text-lg font-semibold tracking-tight">Tell us about you</h3>
            <p className="text-sm text-oth-ink3 font-body mt-1 mb-6">
                Everything here is optional unless marked <span className="text-red-600">*</span>. It stays encrypted with your plan — One Tree Hill cannot see it unless you choose to submit.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {questions.map((q) => (
                    <QuestionField
                        key={q.id} q={q}
                        value={profile[q.id]}
                        onChange={(v) => setProfile({ ...profile, [q.id]: v })}
                        error={errors?.[q.id]}
                    />
                ))}
            </div>
        </div>
    );
}
